import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { orderBy, has, set, get, split } from 'lodash/fp';
import { Icon, IconButton, Table, Tooltip, Whisper } from 'rsuite';
import styled from 'styled-components';
import Pagination from '../../Pagination';
import DateCell from '../../widgets/custom-cell/Date';
import * as PERMISSIONS from '../../../constants/permissions/reservation';
import { modifyAccess } from '../../../helpers/permission';
import AccessControl from '../../AccessControl';
import PaginationType from '../../../interfaces/PaginationType';
import CustomHeaderCell from '../../widgets/custom-cell/HeaderCell';
import AutoHeightTable from '../../AutoHeightTable';
import PathKeyCell from '../../widgets/custom-cell/PathKey';
import StatusCell from '../../reservations/List/StatusCell';
import ProgramsCell from '../../reservations/List/ProgramsCell';
import pagePathToUrl from '../../../helpers/pagePathToUrl';
import {
  reservationsEditPath,
  reservationsViewPath
} from '../../../routes/Paths';
import redirectTo from '../../../helpers/redirectTo';
import history from '../../../routes/history';

const { Column, HeaderCell, Cell } = Table;

const StyledCell = styled(Cell)`
  & > .rs-table-cell-content {
    padding: 5px 10px;
  }
`;

const ActionCell = ({ rowData, handleView, handleEdit, ...props }) => (
  <StyledCell {...props} className="link-group">
    <AccessControl allowOnlyFor={modifyAccess(PERMISSIONS.MODULE)}>
      <Whisper
        placement="top"
        trigger="hover"
        speaker={<Tooltip>Edit</Tooltip>}
      >
        <IconButton
          appearance="link"
          onClick={() => {
            handleEdit(rowData.id);
          }}
          icon={<Icon icon="edit2" />}
        />
      </Whisper>
    </AccessControl>
    <Whisper placement="top" trigger="hover" speaker={<Tooltip>View</Tooltip>}>
      <IconButton
        appearance="link"
        onClick={() => {
          handleView(rowData.id);
        }}
        icon={<Icon icon="eye" />}
      />
    </Whisper>
  </StyledCell>
);
ActionCell.propTypes = {
  rowData: PropTypes.shape({
    id: PropTypes.number
  }),
  handleView: PropTypes.func.isRequired,
  handleEdit: PropTypes.func.isRequired
};

ActionCell.defaultProps = {
  rowData: null
};

class GuestReservationList extends Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  componentDidMount() {
    const { onInit, isFetched } = this.props;
    if (!isFetched) {
      onInit();
    }
  }

  getData() {
    const { data } = this.props;
    const { sortColumn, sortType } = this.state;
    return orderBy(sortColumn, sortType, data);
  }

  handleSortColumn = (sortColumn, sortType) => {
    const { onSort, query } = this.props;
    onSort(sortColumn, sortType, query);
  };

  handleChangePage = page => {
    const { onChangePage, loadedPages, query } = this.props;
    const isPageLoaded = has(page, loadedPages);
    const nextPageQuery = set('page', page, query);
    onChangePage(nextPageQuery, isPageLoaded);
  };

  handleChangeLimit = limit => {
    const { onChangeLimit, query } = this.props;
    const newQuery = { ...query, limit, page: 1 };
    onChangeLimit(newQuery);
  };

  handleView = reservationId => {
    const { isLegacyView } = this.props;
    const reservationView = pagePathToUrl(reservationsViewPath, {
      id: reservationId
    });
    if (isLegacyView) {
      redirectTo(reservationView);
    } else {
      history.push(reservationView);
    }
  };

  handleEdit = reservationId => {
    const { isLegacyView } = this.props;
    const reservationEdit = pagePathToUrl(reservationsEditPath, {
      id: reservationId
    });
    if (isLegacyView) {
      redirectTo(reservationEdit);
    } else {
      history.push(reservationEdit);
    }
  };

  render() {
    const { isFetching, query, pagination, isLegacyView } = this.props;
    const [sortColumn, sortType] = split(':', get('order', query));
    const deductColWidth = isLegacyView ? 20 : 0;
    return (
      <Fragment>
        <AutoHeightTable
          bordered
          reduceHeight={540}
          loading={isFetching}
          data={this.getData()}
          sortColumn={sortColumn}
          sortType={sortType}
          onSortColumn={this.handleSortColumn}
        >
          <Column width={100 - deductColWidth} sortable resizable>
            <CustomHeaderCell>ID</CustomHeaderCell>
            <Cell dataKey="id" />
          </Column>
          <Column width={150 - deductColWidth} sortable resizable>
            <CustomHeaderCell>Date/Time</CustomHeaderCell>
            <DateCell dataKey="requestTime" format="MM/DD/YYYY hh:mm A" />
          </Column>
          <Column width={110 - deductColWidth} sortable>
            <CustomHeaderCell>Status</CustomHeaderCell>
            <StatusCell dataKey="status" />
          </Column>
          {!isLegacyView && (
            <Column width={100} sortable>
              <CustomHeaderCell>Barcode</CustomHeaderCell>
              <Cell dataKey="code" />
            </Column>
          )}
          <Column width={150 - deductColWidth} resizable>
            <CustomHeaderCell>Outreach</CustomHeaderCell>
            <PathKeyCell dataKey="outreach.name" />
          </Column>
          <Column width={170 - deductColWidth} resizable>
            <CustomHeaderCell>Tracking Method</CustomHeaderCell>
            <ProgramsCell dataKey="programs" />
          </Column>
          <Column width={100} fixed="right" align="center">
            <HeaderCell>Action</HeaderCell>
            <ActionCell
              dataKey="id"
              handleView={this.handleView}
              handleEdit={this.handleEdit}
            />
          </Column>
        </AutoHeightTable>

        <Pagination
          page={pagination.currentPage}
          limit={pagination.limit}
          total={pagination.totalCount}
          onChangePage={this.handleChangePage}
          onChangeLimit={this.handleChangeLimit}
        />
      </Fragment>
    );
  }
}

GuestReservationList.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isFetching: PropTypes.bool.isRequired,
  isFetched: PropTypes.bool.isRequired,
  onInit: PropTypes.func.isRequired,
  query: PropTypes.shape({}).isRequired,
  loadedPages: PropTypes.shape({}).isRequired,
  pagination: PaginationType.isRequired,
  onChangePage: PropTypes.func.isRequired,
  onChangeLimit: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
  isLegacyView: PropTypes.bool
};

GuestReservationList.defaultProps = {
  isLegacyView: false
};

export default GuestReservationList;
